/**
 * Finance V2 — BU Allocation & Performance.
 *
 * Replaces the single-pool BU Allocation with a per-expense allocation matrix
 * per the Feb 2026 rebuild brief:
 *   • Every 6-xxxx overhead account is a row
 *   • Each account can pick Revenue % / Headcount / Manual %
 *   • Live reconciliation of total vs allocated vs unallocated
 *   • Full BU Performance waterfall (before + after overhead)
 *   • Drill-down: click any BU's overhead total → see expense-by-expense breakdown
 */
import React, { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, DollarSign, TrendingUp, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";

const fmtK = (n) => n === undefined || n === null ? "—"
  : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);
const fmtPct = (n) => n === undefined || n === null ? "—" : `${Number(n).toFixed(1)}%`;

export default function FinanceV2Page() {
  // Legacy standalone page — kept for backward-compat routing at
  // /finance/v2/bu-allocation. The Finance page now embeds the same panel
  // inline via <BUExpensesAllocationContent />.
  return (
    <AppLayout title="BU Expenses Allocation · V2"
               subtitle="Per-expense overhead allocation + full BU waterfall">
      <BUExpensesAllocationContent />
    </AppLayout>
  );
}


export function BUExpensesAllocationContent() {
  const [fy, setFy] = useState(2026);
  const [tab, setTab] = useState("allocation");
  const [expandedBUs, setExpandedBUs] = useState({});
  const [busy, setBusy] = useState(false);
  const [register, setRegister] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [perf, setPerf] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [manualEditor, setManualEditor] = useState(null);   // { account_no, account_name, weights: {bu_id: %} }

  const load = async () => {
    setBusy(true);
    try {
      const [reg, mtx, prf] = await Promise.all([
        api.get(`/finance/v2/overhead-register?fy_year=${fy}`),
        api.get(`/finance/v2/allocation-matrix?fy_year=${fy}`),
        api.get(`/finance/v2/bu-performance?fy_year=${fy}`),
      ]);
      setRegister(reg.data); setMatrix(mtx.data); setPerf(prf.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy]);

  const applyMethod = async (account_no, method) => {
    // Manual needs per-BU weights → open editor instead of POSTing empty
    if (method === "manual") {
      const row = matrix?.rows.find((r) => r.account_no === account_no);
      const cur = row?.cells || {};
      const seed = {};
      (matrix?.business_units || []).forEach((b) => {
        seed[b._id] = cur[b._id]?.pct ?? 0;
      });
      setManualEditor({
        account_no,
        account_name: row?.account_name,
        weights: seed,
      });
      return;
    }
    try {
      await api.post(`/finance/v2/allocation/${account_no}?fy_year=${fy}`, { method });
      toast.success(`${account_no} → ${method}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveManual = async () => {
    if (!manualEditor) return;
    const total = Object.values(manualEditor.weights).reduce((a, b) => a + Number(b || 0), 0);
    if (total <= 0) { toast.error("Weights must sum to > 0"); return; }
    try {
      await api.post(`/finance/v2/allocation/${manualEditor.account_no}?fy_year=${fy}`,
        { method: "manual", weights: manualEditor.weights, notes: `Manual · sum ${total.toFixed(1)}%` });
      toast.success(`${manualEditor.account_no} · manual weights saved`);
      setManualEditor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const bulkApply = async (method) => {
    if (!register?.accounts.length) return;
    if (!confirm(`Apply "${method}" to ALL ${register.accounts.length} overhead accounts?`)) return;
    try {
      await api.post(`/finance/v2/allocation/bulk?fy_year=${fy}`, {
        account_nos: register.accounts.map((a) => a.account_no), method,
      });
      toast.success(`Applied ${method} to ${register.accounts.length} accounts`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk save failed"); }
  };

  const openDrilldown = async (bu_id, bu_code) => {
    try {
      const { data } = await api.get(`/finance/v2/bu-performance/${bu_id}/breakdown?fy_year=${fy}`);
      setDrilldown({ ...data, bu_code });
    } catch (e) { toast.error(e.response?.data?.detail || "Drill-down failed"); }
  };

  const rec = register?.reconciliation;
  const bus = matrix?.business_units || [];

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs text-slate-600">FY</label>
        <Select value={String(fy)} onValueChange={(v) => setFy(Number(v))}>
          <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>FY{y % 100}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
        <div className="ml-auto inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
          {[
            { v: "allocation", label: "Allocation Matrix" },
            { v: "performance", label: "BU Performance" },
          ].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)}
                    data-testid={`v2-tab-${t.v}`}
                    className={`px-3 py-1 border-r border-slate-200 last:border-r-0 transition ${
                      tab === t.v ? "bg-gbe text-white" : "bg-white text-slate-700 hover:bg-slate-50"
                    }`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Reconciliation strip */}
      {rec && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <StatCard label="Total overhead" val={fmtK(rec.total_overhead)} tone="slate" />
          <StatCard label="Allocated" val={fmtK(rec.total_allocated)} tone="emerald"
                    sub={`${rec.accounts_allocated} of ${rec.accounts_total} accounts`} />
          <StatCard label="Unallocated" val={fmtK(rec.unallocated)}
                    tone={rec.unallocated > 0 ? "amber" : "slate"} />
          <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col justify-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Bulk-apply method</div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="text-[10px] h-7"
                      onClick={() => bulkApply("revenue_pct")} data-testid="v2-bulk-revenue">Revenue %</Button>
              <Button size="sm" variant="outline" className="text-[10px] h-7"
                      onClick={() => bulkApply("headcount")} data-testid="v2-bulk-headcount">Headcount</Button>
            </div>
          </div>
        </div>
      )}

      {/* --- ALLOCATION MATRIX --- */}
      {tab === "allocation" && matrix && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Account</th>
                <th className="text-right px-3 py-2">FY Total</th>
                <th className="text-left px-3 py-2">Method</th>
                {bus.map((b) => (
                  <th key={b._id} className="text-right px-3 py-2 whitespace-nowrap">
                    <span className="font-mono text-slate-500">{b.code}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="v2-matrix-body">
              {matrix.rows.map((r) => (
                <tr key={r.account_no} className={`border-b border-slate-100 hover:bg-slate-50/50 ${!r.is_allocated ? "bg-amber-50/40" : ""}`}>
                  <td className="px-3 py-1.5 sticky left-0 bg-inherit">
                    <span className="font-mono text-[10px] text-slate-500 mr-1">{r.account_no}</span>
                    {r.account_name}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtK(r.fy_total)}</td>
                  <td className="px-3 py-1.5">
                    <Select value={r.method || ""} onValueChange={(v) => applyMethod(r.account_no, v)}>
                      <SelectTrigger className="h-7 w-32 text-[10px]" data-testid={`v2-method-${r.account_no}`}>
                        <SelectValue placeholder="— unallocated —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="revenue_pct">Revenue %</SelectItem>
                        <SelectItem value="headcount">Headcount</SelectItem>
                        <SelectItem value="manual">Manual %</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  {bus.map((b) => {
                    const c = r.cells[b._id] || { pct: 0, dollars: 0 };
                    return (
                      <td key={b._id} className="px-2 py-1.5 text-right tabular-nums">
                        {c.dollars > 0 ? (
                          <div>
                            <div className="text-slate-800">{fmtK(c.dollars)}</div>
                            <div className="text-[9px] text-slate-400">{fmtPct(c.pct)}</div>
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Totals row */}
              <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                <td className="px-3 py-2 sticky left-0 bg-slate-100">Total</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtK(rec?.total_overhead)}</td>
                <td></td>
                {bus.map((b) => (
                  <td key={b._id} className="px-2 py-2 text-right tabular-nums">
                    {fmtK(matrix.bu_totals[b._id] || 0)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* --- BU PERFORMANCE --- */}
      {tab === "performance" && perf && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">BU</th>
                <th className="text-right px-3 py-2">Revenue</th>
                <th className="text-right px-3 py-2">Direct Costs</th>
                <th className="text-right px-3 py-2 font-semibold">Gross Profit</th>
                <th className="text-right px-3 py-2">Direct OpEx</th>
                <th className="text-right px-3 py-2 font-semibold">Contribution (before OH)</th>
                <th className="text-right px-3 py-2 text-rose-600">Allocated Overhead</th>
                <th className="text-right px-3 py-2 font-bold">Fully Alloc. Contribution</th>
                <th className="text-right px-3 py-2">GM % (bef.)</th>
                <th className="text-right px-3 py-2">GM % (aft.)</th>
              </tr>
            </thead>
            <tbody data-testid="v2-perf-body">
              {perf.rows.map((r) => (
                <React.Fragment key={r.business_unit_id}>
                <tr className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 sticky left-0 bg-inherit whitespace-nowrap">
                    {r.children?.length ? (
                      <button onClick={() => setExpandedBUs((p) => ({ ...p, [r.business_unit_id]: !p[r.business_unit_id] }))}
                              data-testid={`v2-expand-${r.bu_code}`}
                              className="mr-1 text-slate-400 hover:text-slate-700 font-mono">
                        {expandedBUs[r.business_unit_id] ? "▾" : "▸"}
                      </button>
                    ) : <span className="mr-1 inline-block w-3" />}
                    <span className="font-mono text-[10px] text-slate-500 mr-1">{r.bu_code}</span>
                    {r.bu_name}
                    {r.children?.length ? <span className="ml-1.5 text-[9px] uppercase tracking-wide text-slate-400">{r.children.length - 1} sub-BU{r.children.length > 2 ? "s" : ""} rolled in</span> : null}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtK(r.revenue)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtK(r.direct_costs)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtK(r.gross_profit)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtK(r.direct_opex)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtK(r.contribution_before_oh)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">
                    <button onClick={() => openDrilldown(r.business_unit_id, r.bu_code)}
                            className="hover:underline cursor-pointer"
                            data-testid={`v2-drill-${r.bu_code}`}
                            title="Click to see expense-by-expense breakdown">
                      {fmtK(r.allocated_overhead)} ↗
                    </button>
                  </td>
                  <td className={`px-3 py-1.5 text-right tabular-nums font-bold ${r.fully_allocated_contribution < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {fmtK(r.fully_allocated_contribution)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{fmtPct(r.margin_pct_before_oh)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmtPct(r.margin_pct_after_oh)}</td>
                </tr>
                {expandedBUs[r.business_unit_id] && (r.children || []).map((c) => (
                  <tr key={`${r.business_unit_id}-${c.business_unit_id}-${c._self ? "self" : "child"}`}
                      data-testid={`v2-child-${c.bu_code}${c._self ? "-direct" : ""}`}
                      className="border-b border-slate-50 bg-slate-50/60 text-slate-500">
                    <td className="px-3 py-1 sticky left-0 bg-slate-50 pl-9 whitespace-nowrap">
                      <span className="font-mono text-[10px] mr-1">{c.bu_code}</span>
                      {c.bu_name}{c._self ? " (direct)" : ""}
                    </td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtK(c.revenue)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtK(c.direct_costs)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtK(c.gross_profit)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtK(c.direct_opex)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtK(c.contribution_before_oh)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtK(c.allocated_overhead)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtK(c.fully_allocated_contribution)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtPct(c.margin_pct_before_oh)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{fmtPct(c.margin_pct_after_oh)}</td>
                  </tr>
                ))}
                </React.Fragment>
              ))}
              <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                <td className="px-3 py-2 sticky left-0 bg-slate-100">Total</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtK(perf.totals.revenue)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtK(perf.totals.direct_costs)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtK(perf.totals.gross_profit)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtK(perf.totals.direct_opex)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtK(perf.totals.contribution_before_oh)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtK(perf.totals.allocated_overhead)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtK(perf.totals.fully_allocated_contribution)}</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Drill-down modal */}
      {drilldown && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
             onClick={() => setDrilldown(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mt-8 max-h-[85vh] flex flex-col overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Overhead breakdown · <span className="font-mono">{drilldown.bu_code}</span></div>
                <div className="text-[10px] text-slate-500">Total allocated: {fmtK(drilldown.total_allocated_to_bu)}</div>
              </div>
              <button onClick={() => setDrilldown(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Account</th>
                    <th className="text-left px-3 py-2">Method</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">% to this BU</th>
                    <th className="text-right px-3 py-2 font-semibold">$ to this BU</th>
                  </tr>
                </thead>
                <tbody>
                  {drilldown.lines.map((l, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-3 py-1.5">
                        <span className="font-mono text-[10px] text-slate-500 mr-1">{l.account_no}</span>
                        {l.account_name}
                      </td>
                      <td className="px-3 py-1.5 text-[10px] uppercase text-slate-500">{l.method?.replace("_", " ")}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{fmtK(l.account_total)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtPct(l.pct_allocated_to_this_bu)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtK(l.dollars_to_this_bu)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* Manual weights editor modal */}
      {manualEditor && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
             onClick={() => setManualEditor(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mt-8 max-h-[85vh] flex flex-col overflow-hidden"
               onClick={(e) => e.stopPropagation()}
               data-testid="v2-manual-modal">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Manual allocation weights</div>
                <div className="text-[11px] text-slate-500">
                  <span className="font-mono">{manualEditor.account_no}</span> · {manualEditor.account_name}
                </div>
              </div>
              <button onClick={() => setManualEditor(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-[11px] text-slate-500 mb-3">
                Enter the % of this account's overhead to allocate to each BU. Values are auto-normalised to 100% on save.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(matrix?.business_units || []).map((b) => (
                  <label key={b._id} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5">
                    <span className="font-mono text-[10px] text-slate-500 w-16">{b.code}</span>
                    <span className="flex-1 truncate text-slate-700">{b.name}</span>
                    <input type="number" step="0.1" min="0" max="100"
                           value={manualEditor.weights[b._id] ?? 0}
                           onChange={(e) => setManualEditor({
                             ...manualEditor,
                             weights: { ...manualEditor.weights, [b._id]: Number(e.target.value) },
                           })}
                           data-testid={`v2-manual-input-${b.code}`}
                           className="w-20 h-7 text-right text-xs border border-slate-200 rounded px-2" />
                    <span className="text-[10px] text-slate-400">%</span>
                  </label>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-600">
                  Sum: <span className="font-mono font-semibold">
                    {Object.values(manualEditor.weights).reduce((a, b) => a + Number(b || 0), 0).toFixed(1)}%
                  </span>
                  <span className="text-[10px] text-slate-400 ml-2">(will auto-normalise to 100%)</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setManualEditor(null)}
                          data-testid="v2-manual-cancel">Cancel</Button>
                  <Button size="sm" onClick={saveManual}
                          data-testid="v2-manual-save"
                          className="bg-gbe hover:bg-gbe-dark text-white">Save weights</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatCard({ label, val, sub, tone = "slate" }) {
  const tones = {
    slate: "bg-white border-slate-200",
    emerald: "bg-emerald-50/60 border-emerald-200",
    amber: "bg-amber-50/60 border-amber-200",
  };
  return (
    <div className={`rounded-lg border ${tones[tone]} p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-800 tabular-nums mt-1">{val}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
